import { sdk } from '@/services/sdk';

const { eq } = window.ApperSDK.CoreSDK;

const USER_FIELDS = ['FirstName', 'LastName', 'Name', 'Email'].map((name) => ({ field: { Name: name } }));

export const EMAIL_RE = /^\S+@\S+\.\S+$/;

export const lowerEmail = (v) => String(v ?? '').trim().toLowerCase();

const errText = (err, fallback) => err?.message || fallback;

/** "Maya Okafor Jr" -> { firstName: 'Maya', lastName: 'Okafor Jr' } */
export function splitName(full) {
  const parts = String(full ?? '').trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] ?? '', lastName: parts.slice(1).join(' ') };
}

export const joinName = (firstName, lastName) => [firstName, lastName].map((s) => String(s ?? '').trim()).filter(Boolean).join(' ');

/** Every sign-in account on the app (team and customers). Owner only. */
export async function listAccounts() {
  const r = await sdk.admin.fetch('user', { pagingInfo: { limit: 500, offset: 0 }, fields: USER_FIELDS });
  if (!r?.success) throw new Error(r?.message || 'Could not load sign-in accounts');
  return r.data ?? [];
}

/**
 * The User record behind a team member or customer. Team member ids are not always the
 * User id, so the email is the first match and the id is the fallback.
 */
export async function findAccount({ id, email }) {
  const wanted = lowerEmail(email);
  try {
    const all = await listAccounts();
    const hit =
      (wanted && all.find((u) => lowerEmail(u.Email) === wanted)) ||
      (id != null && all.find((u) => String(u.Id) === String(id)));
    if (hit) return hit;
  } catch {
    // fall through to a direct read
  }
  if (id != null && id !== '') {
    try {
      const r = await sdk.admin.get('user', id);
      if (r?.success && r.data) return r.data;
    } catch {
      // not found
    }
  }
  return null;
}

async function tryUpdate(fields) {
  try {
    const res = await sdk.admin.update('user', fields);
    return res?.success ? { ok: true } : { ok: false, message: res?.message || 'The account was not saved' };
  } catch (err) {
    return { ok: false, message: errText(err, 'The account was not saved') };
  }
}

/**
 * Save names (and the email when it changed) on a sign-in account.
 * When the platform refuses the email, the names are saved on their own and
 * `emailRejected` explains why. Never throws.
 */
export async function saveAccount(account, { firstName, lastName, email }) {
  const FirstName = String(firstName ?? '').trim();
  const LastName = String(lastName ?? '').trim();
  const base = { Id: account.Id, FirstName, LastName, Name: joinName(FirstName, LastName) };
  const nextEmail = lowerEmail(email);
  const wantsEmail = !!nextEmail && nextEmail !== lowerEmail(account.Email);

  let emailRejected = '';
  let res = await tryUpdate(wantsEmail ? { ...base, Email: nextEmail } : base);
  if (!res.ok && wantsEmail) {
    emailRejected = res.message;
    res = await tryUpdate(base);
  }
  if (!res.ok) return { ok: false, message: res.message };

  if (wantsEmail && !emailRejected) {
    try {
      const fresh = await sdk.admin.get('user', account.Id);
      if (fresh?.success && fresh.data?.Email && lowerEmail(fresh.data.Email) !== nextEmail) {
        emailRejected = 'The sign-in email is managed by the platform and stayed the same.';
      }
    } catch {
      // the write reported success; trust it
    }
  }

  const emailChanged = wantsEmail && !emailRejected;
  return {
    ok: true,
    emailChanged,
    emailRejected,
    name: base.Name,
    email: emailChanged ? nextEmail : lowerEmail(account.Email),
  };
}

/** Keep the customers_c record that matches a sign-in email in step with the account. Best effort. */
export async function syncCustomerRecord(oldEmail, { name, email }) {
  const raw = String(oldEmail ?? '').trim();
  if (!raw) return;
  const lookup = async (value) => {
    const r = await sdk.table('customers_c').select(['Name', 'email_c']).where(eq('email_c', value)).limit(1).fetch();
    return r?.success ? (r.data?.[0] ?? null) : null;
  };
  try {
    const rec = (await lookup(raw)) || (raw !== lowerEmail(raw) ? await lookup(lowerEmail(raw)) : null);
    if (!rec) return;
    const patch = { Id: rec.Id };
    if (name && name !== rec.Name) patch.Name = name;
    if (email && lowerEmail(email) !== lowerEmail(rec.email_c)) patch.email_c = lowerEmail(email);
    if (Object.keys(patch).length > 1) await sdk.table('customers_c').update(patch);
  } catch {
    // the account itself already saved
  }
}
